import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  SAM_APP_CODE,
  SAM_ROLE_STRATEGY_ADMIN,
  SRO_ROLE_COMM_MGR,
  SRO_ROLE_SBM
} from '../AppConstants';
import { fetchJsonWithAuth } from '../fetch-json.util';
import { getEnvVar } from '../../utils/getEnvVar';

export type RolesStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface RolesState {
  strategyAdmins: string[] | null;
  sroSBMs: string[] | null;
  sroCommMgrs: string[] | null;
  status: RolesStatus;
}

const initialState: RolesState = {
  strategyAdmins: null,
  sroSBMs: null,
  sroCommMgrs: null,
  status: 'idle'
};

/**
 * Extracts a `string[]` from a `fetchJsonWithAuth` response.
 *
 * `fetchJsonWithAuth` resolves for HTTP 200–404, so we must validate the
 * status ourselves.  When the status is non-2xx we throw so the thunk is
 * rejected.  When the body is not an array (e.g. an error-object payload) we
 * normalise to `[]` rather than letting a non-array reach downstream callers
 * that assume `.forEach` is available.
 */
function toStringArray(resp: {
  json: unknown;
  meta: { status: number; url: string };
}): string[] {
  if (resp.meta.status < 200 || resp.meta.status >= 300) {
    throw new Error(
      `Unexpected HTTP ${resp.meta.status} from ${resp.meta.url}`
    );
  }
  return Array.isArray(resp.json) ? (resp.json as string[]) : [];
}

/**
 * Fetches all three role lists (SAM admins, SRO SBMs, SRO Comm Mgrs) in
 * parallel.  Dispatch this thunk once per session; consumer components should
 * guard with `status === 'idle'` so the API is only hit once regardless of
 * how many pages/components request the data.
 */
export const fetchRoleLists = createAsyncThunk(
  'roles/fetchRoleLists',
  async () => {
    const [adminsResp, sbmsResp, commMgrsResp] = await Promise.all([
      fetchJsonWithAuth(
        `${getEnvVar('VITE_USER_ROLE_API_URL')}/${SAM_APP_CODE}/roles/${SAM_ROLE_STRATEGY_ADMIN}`
      ),
      fetchJsonWithAuth(
        `${getEnvVar('VITE_USER_ROLE_API_URL')}/sro-roles/${SRO_ROLE_SBM}`
      ),
      fetchJsonWithAuth(
        `${getEnvVar('VITE_USER_ROLE_API_URL')}/sro-roles/${SRO_ROLE_COMM_MGR}`
      )
    ]);

    return {
      strategyAdmins: toStringArray(adminsResp),
      sroSBMs: toStringArray(sbmsResp),
      sroCommMgrs: toStringArray(commMgrsResp)
    };
  },
  {
    condition: (_, { getState }) => {
      const { roles } = getState() as { roles: RolesState };
      return roles.status === 'idle' || roles.status === 'error';
    }
  }
);

const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    /** Reset cached roles (e.g. on logout) */
    resetRoles: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoleLists.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchRoleLists.fulfilled, (state, action) => {
        state.strategyAdmins = action.payload.strategyAdmins;
        state.sroSBMs = action.payload.sroSBMs;
        state.sroCommMgrs = action.payload.sroCommMgrs;
        state.status = 'loaded';
      })
      .addCase(fetchRoleLists.rejected, (state) => {
        state.status = 'error';
      });
  }
});
export const { resetRoles } = rolesSlice.actions;
export const rolesReducer = rolesSlice.reducer;
